/**
 * Original source: https://github.com/aplbrain/npyjs/blob/master/index.js
 * License: https://github.com/aplbrain/npyjs/blob/master/LICENSE
 *
 * Since the package has only few monthly downloads and has only few eyes on it
 * it's safer to include it in the codebase instead of as a dependency.
 */

import { assert } from '@brainfocus/web/utils/assert';

// TypedArray constructor types for numpy array parsing
type TypedArrayConstructor =
    | Uint8ArrayConstructor
    | Int8ArrayConstructor
    | Uint16ArrayConstructor
    | Int16ArrayConstructor
    | Uint32ArrayConstructor
    | Int32ArrayConstructor
    | BigUint64ArrayConstructor
    | BigInt64ArrayConstructor
    | Float32ArrayConstructor
    | Float64ArrayConstructor;

export class Npyjs {

    private dtypes: Record<string, {
        name: DtypeName;
        size: number;
        arrayConstructor: TypedArrayConstructor;
    }>;

    constructor() {
        this.dtypes = {
            '<u1': {
                name: 'uint8',
                size: 8,
                arrayConstructor: Uint8Array,
            },
            '|u1': {
                name: 'uint8',
                size: 8,
                arrayConstructor: Uint8Array,
            },
            '<u2': {
                name: 'uint16',
                size: 16,
                arrayConstructor: Uint16Array,
            },
            '|i1': {
                name: 'int8',
                size: 8,
                arrayConstructor: Int8Array,
            },
            '<i2': {
                name: 'int16',
                size: 16,
                arrayConstructor: Int16Array,
            },
            '<u4': {
                name: 'uint32',
                size: 32,
                arrayConstructor: Int32Array,
            },
            '<i4': {
                name: 'int32',
                size: 32,
                arrayConstructor: Int32Array,
            },
            '<u8': {
                name: 'uint64',
                size: 64,
                arrayConstructor: BigUint64Array,
            },
            '<i8': {
                name: 'int64',
                size: 64,
                arrayConstructor: BigInt64Array,
            },
            '<f4': {
                name: 'float32',
                size: 32,
                arrayConstructor: Float32Array,
            },
            '<f8': {
                name: 'float64',
                size: 64,
                arrayConstructor: Float64Array,
            },
        };
    }

    public parse<T = Float32Array>(arrayBufferContents: ArrayBuffer): Npy<T> {
        // const version = arrayBufferContents.slice(6, 8); // Uint8-encoded
        const headerLength = new DataView(arrayBufferContents.slice(8, 10)).getUint8(0);
        const offsetBytes = 10 + headerLength;

        const hcontents = new TextDecoder('utf-8').decode(
            new Uint8Array(arrayBufferContents.slice(10, 10 + headerLength)),
        );
        const header = JSON.parse(
            hcontents
                .toLowerCase() // True -> true
                .replace(/'/g, '"')
                .replace('(', '[')
                .replace(/,*\),*/g, ']'),
        );
        const shape = header.shape;
        const dtype = this.dtypes[header.descr];
        assert(dtype, `Unknown dtype for descriptor ${header.descr}`);
        const nums = new dtype.arrayConstructor(
            arrayBufferContents,
            offsetBytes,
        );
        return {
            dtype: dtype.name,
            data: nums as T,
            shape,
            fortranOrder: header.fortran_order,
        };
    }

}

export interface Npy<T> {
    data: T;
    dtype: DtypeName;
    fortranOrder: false;
    shape: number[];
}

export type DtypeName = 'uint8' | 'uint8' | 'uint16' | 'int8' | 'int16' | 'uint32' | 'int32' | 'uint64' | 'int64' | 'float32' | 'float64';
